import {
  BLTeam,
  CreativeStatus,
  ID,
  RatingInfo,
  RatingValue,
  AnyUser,
  MisleadLevel,
  ConceptType,
} from './model';
import { Genre, Intro } from './apiScenarios';
import { PlotRecognition, PlotRecognitionServer, getPlotRecognition } from './modelPlotRecognition';

export type BLOptions = {
  projectId: ID;
  creativeName: string;
  creativeTypeId: ID;
  managerId: ID;
  developerId: ID;
  manager: AnyUser | null;
  developer: AnyUser | null;
  tagsActive: string[];
  mislead_level: MisleadLevel['id'] | null;
  genres: number[];
  intro: number | null;
  ai_generated: boolean;
  concept_type: ConceptType['id'] | null;
  teamID: ID;
  source_creatives: { name: string; id: number }[] | null;
  // Параметры AI-разметки сюжета, вкладка `Metadata` (MYUA-16423)
  plot_recognition: PlotRecognition;
};

/**
 * Пейлоад `PATCH /bl/creatives/<id>`: у разметки отправляем только изменённые поля,
 * поэтому `plot_recognition` здесь частичный (см. `diffPlotRecognition`).
 */
export type CreativePatchOptions = Omit<Partial<BLOptions>, 'plot_recognition'> & {
  plot_recognition?: Partial<PlotRecognition>;
};

export enum BenchmarkTestIcon {
  sad = 'sad',
  normal = 'normal',
  happy = 'happy',
}

interface ICreativeFolderBase {
  benchmark_test_stage_1_result: null | number;
  benchmark_test_stage_1_icon: null | string;
  benchmark_test_stage_2_result: null | number;
  benchmark_test_stage_2_icon: null | string;
  benchmark_install_adset_id: string;
  benchmark_spend_adset_id: string;
  benchmark_spend_creative_name: string;
  customer_id: number;
  customer: AnyUser | null;
  date_rating: string | null;
  date_start_testing: string | null;
  description: string;
  executor_id: number;
  executor: AnyUser | null;
  id: number;
  install_adset_id: string;
  install_adset_settings: string;
  mislead_level: MisleadLevel['id'] | null;
  node_id: number;
  node_name: string;
  project_id: number;
  spend_adset_id: string;
  spend_adset_settings: string;
  status: CreativeStatus;
  testing_status: string;
  is_benchmark: boolean | null;
  test_ir: number;
  type_id: number;
  ir_av_playtime: null | number;
  av_playtime: null | number;
  benchmark: null | { id: number; node_id: number; node_name: string };
  team: null | BLTeam;
  duration: null | number;
  source_creatives: { name: string; id: number }[];
  has_creative_changes: boolean;
  // `null`, если у креатива ещё не было распознавания сюжета (MYUA-16423)
  plot_recognition: PlotRecognitionServer | null;
  // MYUA-16783: поля есть только у бэкенда с MYUA-16781 (пока лишь dev), поэтому опциональные.
  // `concept_type`: карточка `GET /bl/creatives` отдаёт id, списки
  // (`list_creatives_library` / `list_creatives_hub`) — вложенный `{id, name}`.
  ai_generated?: boolean;
  concept_type?: ConceptType['id'] | ConceptType | null;
}
export type WithTags<T> = T & { tags: string[] };

export type CreativeFolderServer = ICreativeFolderBase & {
  rating: null | number;
  // старое поле — бэкенд продолжает его возвращать для обратной совместимости,
  // мы используем его только как fallback, если `genres` не пришли
  genre: null | number;
  genres: null | number[];
  intro: null | number;
  tags: null | string[];
};

export type CreativeFolderStrict = ICreativeFolderBase & {
  rating: RatingValue;
  // поле оставлено для обратной совместимости со старым бэком; для отображения
  // используется `genres` (массив)
  genre: null | Genre;
  genres: Genre[];
  intro: null | Intro;
};

const isRatingValue = (rating: number | null | RatingValue): rating is RatingValue => {
  if (rating === null) return false;
  if (typeof rating === 'number') return false;
  return RatingValue[rating] !== undefined;
};

function transform(rating: number | null | RatingValue): RatingValue {
  if (isRatingValue(rating)) {
    return rating;
  }
  const keys = Object.keys(RatingInfo) as RatingValue[];
  return keys.find((v) => RatingInfo[v].value === rating) || RatingValue.UNRATED;
}

type ConceptTypeValue = ConceptType['id'] | ConceptType | null | undefined;

/** `concept_type` приходит id (карточка) или `{id, name}` (списки) — приводим к id. */
export const getConceptTypeId = (value: ConceptTypeValue): ConceptType['id'] | null => {
  if (typeof value === 'number') return value;
  return value?.id ?? null;
};

/** Имя типа концепта есть только во вложенном `{id, name}` (списки креативов). */
export const getConceptTypeName = (value: ConceptTypeValue): string | null => {
  if (value === null || value === undefined || typeof value === 'number') return null;
  return value.name;
};

export const getBLOptionsFromCreative = (creative: WithTags<CreativeFolderStrict>): BLOptions => {
  // Если сервер отдал уже `genres` (массив) — используем его; иначе пытаемся
  // собрать массив из старого single-поля `genre` (fallback для старых креативов).
  const genres: number[] =
    creative.genres && creative.genres.length > 0
      ? creative.genres.map((g) => g.id)
      : creative.genre
        ? [creative.genre.id]
        : [];
  return {
    creativeName: creative.node_name,
    creativeTypeId: creative.type_id,
    developerId: creative.executor_id,
    developer: creative.executor,
    managerId: creative.customer_id,
    manager: creative.customer,
    projectId: creative.project_id,
    tagsActive: creative.tags,
    mislead_level: creative.mislead_level,
    genres,
    teamID: creative.team?.id ?? null,
    intro: creative.intro?.id ?? null,
    ai_generated: creative.ai_generated ?? false,
    concept_type: getConceptTypeId(creative.concept_type),
    source_creatives: creative.source_creatives,
    plot_recognition: getPlotRecognition(creative.plot_recognition),
  };
};

export const makeCreativeFolder = (
  creativeFolder: CreativeFolderServer | CreativeFolderStrict,
  teams: Record<BLTeam['id'], BLTeam> | null,
): WithTags<CreativeFolderStrict> => {
  const team = creativeFolder.team;

  // здесь мы можем не знать, какие интро и жанры есть в системе, они могут
  // к этому моменту еще не подгрузиться, поэтому на данном этапе мы создаем
  // объекты-пустышки {name: "<Genre 123>", id: 123}, которых достаточно,
  // чтобы отобразить хоть что-то, но при отображении их в интерфейсе мы уже
  // будем смотреть в стор и в актуальные словари жанров/интро

  // однако __важно__ здесь сохранить их id, так как они потом будут по
  // нему мапиться на реальные словари

  const toGenre = (g: number | Genre): Genre =>
    typeof g === 'number' ? { name: `<Genre ${g}>`, id: g } : g;

  // Приоритет: массив `genres` с сервера. Если его нет или он пустой —
  // fallback на старое single-поле `genre` для обратной совместимости.
  const rawGenres = creativeFolder.genres;
  const genres: Genre[] =
    rawGenres && rawGenres.length > 0
      ? rawGenres.map(toGenre)
      : creativeFolder.genre !== null && creativeFolder.genre !== undefined
        ? [toGenre(creativeFolder.genre)]
        : [];

  return {
    ...creativeFolder,
    intro:
      creativeFolder.intro === null
        ? null
        : typeof creativeFolder.intro === 'number'
          ? { name: `<Intro ${creativeFolder.intro}>`, id: creativeFolder.intro }
          : creativeFolder.intro,
    genre:
      creativeFolder.genre === null || creativeFolder.genre === undefined
        ? null
        : toGenre(creativeFolder.genre),
    genres,

    tags: (creativeFolder as WithTags<CreativeFolderServer>).tags ?? [],
    rating: transform(creativeFolder.rating),
    team: team === null ? null : typeof team === 'number' ? teams?.[team] ?? null : team,
  };
};
